// src/contexts/AuthContext.js
import authService from '../services/authService'; // Your authentication API service
import apiClient from '../api/apiClient'; // Your configured Axios instance
import React, { createContext, useState, useEffect, useCallback, useContext } from 'react'; // Add useContext

// Create the context object that will be used by useAuth and AuthProvider
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Store user object { id, name, email, role }
  const [token, setToken] = useState(() => localStorage.getItem('admin_token')); // Initialize token from localStorage
  const [loading, setLoading] = useState(true); // Loading state for initial auth check
  const [operationLoading, setOperationLoading] = useState(false); // Loading state for specific operations like login/otp
  const [authError, setAuthError] = useState(null);

  // Effect to sync token with localStorage and apiClient headers
  useEffect(() => {
    if (token) {
      localStorage.setItem('admin_token', token);
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user'); // Also remove user from local storage on logout/token clear
      delete apiClient.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Effect to verify token and fetch user profile on initial load or when token is set but user isn't
  useEffect(() => {
    const verifyTokenAndFetchUser = async () => {
      if (token && !user) { // Only run if token exists but user details are missing
        setLoading(true); // For initial load indicator
        try {
          console.log("AuthContext: Verifying token and fetching user...");
          const response = await authService.verifyToken(); // Assumes this uses the token from apiClient headers
          if (response.data && response.data.user) {
            setUser(response.data.user);
            localStorage.setItem('admin_user', JSON.stringify(response.data.user));
            console.log("AuthContext: User fetched and set:", response.data.user);
          } else {
            throw new Error("User data not found in verification response.");
          }
        } catch (error) {
          console.error("AuthContext: Error verifying token or fetching user:", error.response?.data?.message || error.message);
          setToken(null); // Clear invalid token
          setUser(null);  // Clear user
        } finally {
          setLoading(false);
        }
      } else if (!token) {
        // If there's no token, ensure user is cleared and stop loading
        setUser(null);
        setLoading(false);
      } else {
        // Token and user are present, or token is present and user is already fetched
        setLoading(false);
      }
    };

    verifyTokenAndFetchUser();
  }, [token, user]); // Rerun if token changes or user state changes

  // Function for LoginPage.js (or other components) to call after it successfully fetches/receives a token
  const loginAndSetToken = useCallback((newToken, newUserDetails = null) => {
    setAuthError(null);
    setToken(newToken);
    if (newUserDetails) {
      setUser(newUserDetails);
      localStorage.setItem('admin_user', JSON.stringify(newUserDetails));
      console.log("AuthContext: Token and user details directly set.");
    } else {
      console.log("AuthContext: Token set. User details will be fetched by effect if not already present.");
      // The useEffect listening to [token, user] will handle fetching user if !user
    }
  }, []);


  // Login with email and password (using authService)
  const loginWithCredentials = useCallback(async (email, password) => {
    setAuthError(null);
    setOperationLoading(true);
    try {
      const response = await authService.login(email, password);
      if (response.data.access_token && response.data.user) {
        setToken(response.data.access_token);
        setUser(response.data.user);
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
        setOperationLoading(false);
        return true;
      } else {
        throw new Error("Login failed: No token or user data received from authService.login.");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Login via credentials failed.";
      setAuthError(errorMessage);
      setToken(null);
      setUser(null);
      setOperationLoading(false);
      console.error("AuthContext: loginWithCredentials error:", errorMessage);
      return false;
    }
  }, []);

  // Request OTP (using authService)
  const requestOtp = useCallback(async (email) => {
    setAuthError(null);
    setOperationLoading(true);
    try {
      const response = await authService.requestOtp(email);
      setOperationLoading(false);
      return response.data; // Or response.data.message
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to send OTP via context.";
      setAuthError(errorMessage);
      setOperationLoading(false);
      console.error("AuthContext: requestOtp error:", errorMessage);
      return null;
    }
  }, []);

  // Verify OTP (using authService)
  const verifyOtp = useCallback(async (email, otp) => {
    setAuthError(null);
    setOperationLoading(true);
    try {
      const response = await authService.verifyOtp(email, otp);
      if (response.data.access_token && response.data.user) {
        setToken(response.data.access_token);
        setUser(response.data.user);
        localStorage.setItem('admin_user', JSON.stringify(response.data.user));
        setOperationLoading(false);
        return true;
      } else if (response.data) { // OTP verified, but maybe no token/user (e.g., proceed to create password)
        setOperationLoading(false);
        return response.data;
      } else {
        throw new Error("Invalid OTP or unexpected response from authService.verifyOtp.");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Invalid OTP via context.";
      setAuthError(errorMessage);
      setOperationLoading(false);
      console.error("AuthContext: verifyOtp error:", errorMessage);
      return false;
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    console.log("AuthContext: Logging out.");
    setToken(null);
    setUser(null);
    setAuthError(null);
    // localStorage cleanup is handled by the useEffect that listens to 'token'
  }, []);

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!token && !!user, // User must also be present
        user,
        token,
        login: loginAndSetToken, // Exposed as 'login' for LoginPage.js to set token
        loginWithCredentials,    // For components that delegate login API call
        requestOtp,
        verifyOtp,
        logout,
        loading, // For initial page load auth check
        operationLoading, // For specific async operations
        authError,
        setAuthError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Default export the context object itself so src/hooks/useAuth.js can import it
export default AuthContext;